import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useTutor } from './TutorContext';

interface Message {
  id: string;
  text: string;
  type: 'user' | 'bot';
  tutorId: string;
  conversationId: string;
  timestamp: number;
  audioUrl?: string;
  transcript?: string;
  isFromNewResponse?: boolean;
}

interface Conversation {
  id: string;
  tutorId: string;
  messages: Message[];
  lastUpdated: number;
}

interface ConversationsStore {
  [tutorId: string]: Conversation[];
}

interface ChatContextType {
  messages: Message[];
  addMessage: (message: Omit<Message, 'id' | 'timestamp' | 'tutorId' | 'conversationId'>) => void;
  clearMessages: (sessionId?: string) => void;
  switchTutor: (tutorId: string) => void;
  currentConversationId: string;
  isWaitingForAI: boolean;
  setIsWaitingForAI: (waiting: boolean) => void;
  userPlayingId: string | null;
  setUserPlayingId: (id: string | null) => void;
  sessionId: string;
}

const STORAGE_KEY = 'voicebot_conversations';

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [conversations, setConversations] = useState<ConversationsStore>({});
  const [currentConversationId, setCurrentConversationId] = useState<string>(crypto.randomUUID());
  const [messages, setMessages] = useState<Message[]>([]);
  const [isWaitingForAI, setIsWaitingForAI] = useState(false);
  const [userPlayingId, setUserPlayingId] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string>(() => {
    const stored = localStorage.getItem('voicechat_session_id');
    return stored || crypto.randomUUID();
  });
  const { selectedTutor } = useTutor();

  // Load conversations from localStorage on mount
  useEffect(() => {
    const loadConversations = () => {
      if (typeof window !== 'undefined') {
        const savedConversations = localStorage.getItem(STORAGE_KEY);
        if (savedConversations) {
          try {
            const parsed = JSON.parse(savedConversations) as ConversationsStore;
            setConversations(parsed);
          } catch (error) {
            setConversations({});
          }
        }
      }
    };

    loadConversations();
  }, []);

  // Load messages when selectedTutor changes or conversations are loaded
  useEffect(() => {
    if (selectedTutor?.id) {
      const tutorConversations = conversations[selectedTutor.id] || [];
      if (tutorConversations.length > 0) {
        // Load the most recent conversation for this tutor
        const mostRecent = tutorConversations[tutorConversations.length - 1];
        setCurrentConversationId(mostRecent.id);
        // Set isFromNewResponse to false for all loaded messages
        const loadedMessages = mostRecent.messages.map(msg => ({
          ...msg,
          isFromNewResponse: false
        }));
        setMessages(loadedMessages);
      } else {
        // Create a new conversation for this tutor
        const newConversationId = crypto.randomUUID();
        const newConversation = {
          id: newConversationId,
          tutorId: selectedTutor.id,
          messages: [],
          lastUpdated: Date.now()
        };
        
        // Update conversations store with the new conversation
        setConversations(prev => ({
          ...prev,
          [selectedTutor.id]: [...(prev[selectedTutor.id] || []), newConversation]
        }));
        
        setCurrentConversationId(newConversationId);
        setMessages([]);
      }
    }
  }, [selectedTutor?.id]);

  // Save conversations to localStorage whenever they change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(conversations));
    }
  }, [conversations]);

  const addMessage = useCallback((message: Omit<Message, 'id' | 'timestamp' | 'tutorId' | 'conversationId'>) => {
    if (!selectedTutor) return;

    const newMessage: Message = {
      ...message,
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      tutorId: selectedTutor.id,
      conversationId: currentConversationId,
      isFromNewResponse: message.type === 'bot' ? message.isFromNewResponse || false : false
    };

    // Update messages state
    setMessages(prev => [...prev, newMessage]);

    // Update conversations store
    setConversations(prev => {
      const tutorConversations = prev[selectedTutor.id] || [];
      const currentConversationIndex = tutorConversations.findIndex(
        conv => conv.id === currentConversationId
      );

      let newTutorConversations = [...tutorConversations];
      
      if (currentConversationIndex >= 0) {
        // Update existing conversation
        const updatedConversation = {
          ...tutorConversations[currentConversationIndex],
          messages: [...tutorConversations[currentConversationIndex].messages, newMessage],
          lastUpdated: Date.now()
        };
        newTutorConversations[currentConversationIndex] = updatedConversation;
      } else {
        // Create new conversation
        newTutorConversations.push({
          id: currentConversationId,
          tutorId: selectedTutor.id,
          messages: [newMessage],
          lastUpdated: Date.now()
        });
      }

      const updatedConversations = {
        ...prev,
        [selectedTutor.id]: newTutorConversations
      };

      // Save to localStorage immediately
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedConversations));
      }

      return updatedConversations;
    });
  }, [selectedTutor, currentConversationId]);

  const switchTutor = useCallback((tutorId: string) => {
    
    const tutorConversations = conversations[tutorId] || [];
    if (tutorConversations.length > 0) {
      // Load the most recent conversation for the selected tutor
      const mostRecent = tutorConversations[tutorConversations.length - 1];
      setCurrentConversationId(mostRecent.id);
      // Set isFromNewResponse to false for all loaded messages
      const loadedMessages = mostRecent.messages.map(msg => ({
        ...msg,
        isFromNewResponse: false
      }));
      setMessages(loadedMessages);
    } else {
      // Create a new conversation for this tutor
      const newConversationId = crypto.randomUUID();
      const newConversation = {
        id: newConversationId,
        tutorId: tutorId,
        messages: [],
        lastUpdated: Date.now()
      };
      
      // Update conversations store with the new conversation
      setConversations(prev => ({
        ...prev,
        [tutorId]: [...(prev[tutorId] || []), newConversation]
      }));
      
      setCurrentConversationId(newConversationId);
      setMessages([]);
    }
  }, [conversations]);

  const clearMessages = useCallback((newSessionId?: string) => {
    if (!selectedTutor) return;
  
    // Create a new conversation for the current tutor
    const newConversationId = crypto.randomUUID();
    const newConversation = {
      id: newConversationId,
      tutorId: selectedTutor.id,
      messages: [],
      lastUpdated: Date.now()
    };
  
    // Update conversations store, keeping previous conversations
    setConversations(prev => ({
      ...prev,
      [selectedTutor.id]: [...(prev[selectedTutor.id] || []), newConversation]
    }));
  
    setCurrentConversationId(newConversationId);
    setMessages([]);
  
    // Update session ID in state and localStorage
    const finalSessionId = newSessionId || crypto.randomUUID();
    setSessionId(finalSessionId);
    localStorage.setItem('voicechat_session_id', finalSessionId);
  }, [selectedTutor]);

  const value = {
    messages,
    addMessage,
    clearMessages,
    switchTutor,
    currentConversationId,
    isWaitingForAI,
    setIsWaitingForAI,
    userPlayingId,
    setUserPlayingId,
    sessionId,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};
